/**
 * @license
 * Copyright 2026 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 *
 * g3-prettier-ignore-file
 */

/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { interactionsCancel } from "../funcs/interactions-cancel.js";
import { interactionsCreate } from "../funcs/interactions-create.js";
import { interactionsDelete } from "../funcs/interactions-delete.js";
import { interactionsGet } from "../funcs/interactions-get.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import * as interactions from "../models/interactions/index.js";
import * as operations from "../models/operations/index.js";
import {
  CancelInteractionByIdParams,
  CreateAgentInteractionParamsNonStreaming,
  CreateAgentInteractionParamsStreaming,
  CreateInteractionParams,
  CreateModelInteractionParamsNonStreaming,
  CreateModelInteractionParamsStreaming,
  DeleteInteractionParams,
  GetInteractionByIdParams,
  GetInteractionByIdParamsNonStreaming,
  GetInteractionByIdParamsStreaming,
} from "../models/operations/method-params.js";
import { APIPromise, unwrapAsAPIPromise } from "../types/async.js";

export class Interactions extends ClientSDK {
  /**
   * Creating an interaction
   *
   * @remarks
   * Creates a new interaction.
   */
  create(
    params: CreateModelInteractionParamsNonStreaming,
    options?: RequestOptions,
  ): APIPromise<operations.CreateInteractionResponse>;
  create(
    params: CreateAgentInteractionParamsNonStreaming,
    options?: RequestOptions,
  ): APIPromise<operations.CreateInteractionResponse>;
  create(
    params: CreateModelInteractionParamsStreaming,
    options?: RequestOptions,
  ): APIPromise<operations.CreateInteractionResponse>;
  create(
    params: CreateAgentInteractionParamsStreaming,
    options?: RequestOptions,
  ): APIPromise<operations.CreateInteractionResponse>;
  create(
    params: CreateInteractionParams,
    options?: RequestOptions,
  ): APIPromise<operations.CreateInteractionResponse>;
  create(
    params: CreateInteractionParams,
    options?: RequestOptions,
  ): APIPromise<operations.CreateInteractionResponse> {
    const { api_version, ...body } = params;
    return unwrapAsAPIPromise(interactionsCreate(
      this,
      body,
      api_version,
      options,
    ));
  }

  /**
   * Retrieving an interaction
   *
   * @remarks
   * Retrieves the full details of a single interaction based on its `Interaction.id`.
   */
  get(
    id: string,
    params?: GetInteractionByIdParamsNonStreaming,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<operations.GetInteractionByIdResponse>;
  get(
    id: string,
    params: GetInteractionByIdParamsStreaming,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<operations.GetInteractionByIdResponse>;
  get(
    id: string,
    params?: GetInteractionByIdParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<operations.GetInteractionByIdResponse>;
  get(
    id: string,
    params?: GetInteractionByIdParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<operations.GetInteractionByIdResponse> {
    return unwrapAsAPIPromise(interactionsGet(
      this,
      id,
      params?.stream,
      params?.last_event_id,
      params?.include_input,
      params?.api_version,
      options,
    ));
  }

  /**
   * Deleting an interaction
   *
   * @remarks
   * Deletes the interaction by id.
   */
  delete(
    id: string,
    params?: DeleteInteractionParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<void> {
    return unwrapAsAPIPromise(interactionsDelete(
      this,
      id,
      params?.api_version,
      options,
    ));
  }

  /**
   * Canceling an interaction
   *
   * @remarks
   * Cancels an interaction by id. This only applies to background interactions that are still running.
   */
  cancel(
    id: string,
    params?: CancelInteractionByIdParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<interactions.Interaction> {
    return unwrapAsAPIPromise(interactionsCancel(
      this,
      id,
      params?.api_version,
      options,
    ));
  }
}
